import { useMemo, useState } from 'react';
import { AxiosError } from 'axios';

import { useQuery } from '@tanstack/react-query';
import {
  removeEmptyKeysAndReplaceLimitField,
  formatQueryParams,
} from '@components/TableV2/helpers/utils';
import { TableCursorPaginationV2 } from '@components/TableV2/TableCursorPaginationV2';
import { useCursorPagination } from '@hooks/useCursorPagination/useCursorPagination';
import { useI18n } from '@hooks/useI18n/useI18n';
import { useUuidsToPaymentRefsQuery } from '@pages/Contracts/queries/useUuidsToPaymentRefsQuery';
import { IOpsPortalMinorArrears } from '@schemas/opsPortal/types/minorArrears';
import { QueryKeys } from '@utils/constants';
import { enrichWithPaymentRefsAndExternalUuids } from '@utils/enrichWithPaymentRefsAndExternalUuids';
import { Endpoints } from '@utils/enums';
import { fetchDataFromEndpoint } from '@utils/fetchDataFromEndpoint/fetchDataFromEndpoint';
import { useGetMinorArrearsColumns } from './useMinorArrearsColumns';

export const MinorArrearsContracts = () => {
  const { translate } = useI18n();
  const paginationSettings = useCursorPagination();

  const [uuids, setUuids] = useState<string[]>([]);

  const queryParams = useMemo(
    () =>
      removeEmptyKeysAndReplaceLimitField({
        ...paginationSettings.paginationParams,
      }),
    [paginationSettings.paginationParams],
  );

  const stringifiedQueryParams = useMemo(
    () => formatQueryParams(queryParams as object),
    [queryParams],
  );

  const queryKey = useMemo(
    () => [QueryKeys.MINOR_ARREARS_LIST, stringifiedQueryParams],
    [stringifiedQueryParams],
  );

  const {
    data: minorArrearsData,
    error: minorArrearsError,
    isLoading: isMinorArrearsLoading,
  } = useQuery<IOpsPortalMinorArrears, AxiosError>(
    [queryKey],
    () =>
      fetchDataFromEndpoint(Endpoints.FetchMinorArrearsContracts, {
        query: {
          ...(queryParams as object),
        },
      }),
    {
      onSuccess: responseData => {
        paginationSettings.setNextCursor(responseData.next_cursor ?? '');
        paginationSettings.setPrevCursor(responseData.previous_cursor ?? '');
        setUuids(responseData.results.map(item => item.contract_uuid));
      },
    },
  );

  const columns = useGetMinorArrearsColumns();

  const {
    data: uuidPaymentRefMap,
    error: uuidPaymentRefMapError,
    isLoading: isLoadingUuidPaymentRefMap,
  } = useUuidsToPaymentRefsQuery(uuids);

  const data = useMemo(
    () =>
      enrichWithPaymentRefsAndExternalUuids(
        minorArrearsData ?? ({} as IOpsPortalMinorArrears),
        uuidPaymentRefMap?.results ?? [],
      ),
    [minorArrearsData, uuidPaymentRefMap],
  );

  return (
    <TableCursorPaginationV2
      columns={columns}
      count={minorArrearsData?.count ?? 0}
      data={data.results}
      error={minorArrearsError ?? uuidPaymentRefMapError}
      isLoading={isMinorArrearsLoading ?? isLoadingUuidPaymentRefMap}
      name="MinorArrears"
      paginationSettings={paginationSettings}
      title={translate('OPS_PORTAL.TITLE.MINOR_ARREARS')}
      wrapTableInCard={true}
    />
  );
};
